import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { call, client, result, type Schema } from "./api";
import { SvorActionProtocol } from "./SvorActionProtocol";
import {
  cleanMapping, filterRows, formula, importedSource, kindLabels, kindNotes, kinds, mappingProblems, pendingSources,
  quantityCell, remarkSummary, replacedSources, roles, roundHistoryText, setStateText, stateClass, stateLabels,
  type Mapping, type QuantityColumn, type RowFilter, type SourceKind,
} from "./svor";
import "./svor.css";

type SetOut = Schema["SvorSetOut"];
type SourceOut = Schema["SvorSourceOut"];
type RoundOut = Schema["SvorRoundOut"];
type Action = (fn: () => Promise<unknown>) => Promise<void>;

const date = (iso: string | null | undefined) => iso
  ? new Date(iso).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" })
  : "—";
const fileSize = (size: number) => size > 1024 * 1024
  ? `${(size / 1024 / 1024).toLocaleString("ru-RU", { maximumFractionDigits: 1 })} МБ`
  : `${Math.max(1, Math.round(size / 1024))} КБ`;
const message = (error: unknown) => error instanceof Error ? error.message : String(error);

export function SvorPanel({ revisionId, canEdit, sections }: { revisionId: string; canEdit: boolean; sections: string[] }) {
  const cache = useQueryClient();
  const [selectedId, setSelectedId] = useState("");
  const sets = useQuery({
    queryKey: ["svor-sets", revisionId],
    queryFn: () => result(client.GET("/api/v1/revisions/{revision_id}/svor-sets", { params: { path: { revision_id: revisionId } } })),
  });
  const list = sets.data ?? [];
  const current = list.find(item => item.id === selectedId) ?? list[0];
  return <section className="svor-panel" aria-labelledby="svor-title">
    <header className="svor-heading">
      <div>
        <h2 id="svor-title">СВОР · сопоставительная ведомость объёмов работ</h2>
        <p>Одна СВОР на раздел сметы. Выберите исходную смету, исключения (И) и добавления (Д) —
          отдельные файлы или листы одной книги XLSX. {formula}.</p>
      </div>
    </header>
    {sets.isPending && <p role="status" className="muted">Загружаем разделы…</p>}
    {sets.error && <p role="alert" className="error">{message(sets.error)}</p>}
    <div className="svor-sections">
      {list.length > 0 && <label className="field"><span>Раздел сметы</span>
        <select value={current?.id ?? ""} onChange={event => setSelectedId(event.target.value)}>
          {list.map(item => <option key={item.id} value={item.id}>
            {item.section_code}{item.section_title ? ` · ${item.section_title}` : ""} · {setStateText(item)}
          </option>)}
        </select>
      </label>}
      {canEdit && <CreateSet revisionId={revisionId} sections={sections} created={async created => {
        await cache.invalidateQueries({ queryKey: ["svor-sets", revisionId] });
        setSelectedId(created.id);
      }} />}
    </div>
    {sets.isSuccess && list.length === 0 && <div className="empty">
      <h2>СВОР ещё не создана</h2>
      <p>{canEdit
        ? "Укажите раздел сметы, например АР или 02-01-02, и загрузите исходную смету этого раздела."
        : "Разделы СВОР для этой редакции ещё не заведены."}</p>
    </div>}
    {current && <SvorSet key={current.id} setId={current.id} revisionId={revisionId} canEdit={canEdit} />}
  </section>;
}

function CreateSet({ revisionId, sections, created }: { revisionId: string; sections: string[]; created: (set: SetOut) => Promise<void> }) {
  const [code, setCode] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    const sectionCode = code.trim();
    if (!sectionCode || busy) return;
    setBusy(true); setError("");
    try {
      const set = await result(client.POST("/api/v1/revisions/{revision_id}/svor-sets", {
        params: { path: { revision_id: revisionId } },
        body: { section_code: sectionCode, section_title: title.trim() },
      }));
      setCode(""); setTitle("");
      await created(set);
    } catch (cause) { setError(message(cause)); }
    finally { setBusy(false); }
  };
  return <form className="svor-create" aria-label="Новая СВОР раздела" onSubmit={event => { event.preventDefault(); void submit(); }}>
    <label className="field"><span>Код раздела сметы</span>
      <input value={code} list="svor-section-codes" maxLength={64} required placeholder="АР или 02-01-02" disabled={busy}
        onChange={event => setCode(event.target.value)} />
      <datalist id="svor-section-codes">{sections.map(section => <option key={section} value={section} />)}</datalist>
    </label>
    <label className="field"><span>Название раздела (необязательно)</span>
      <input value={title} maxLength={300} placeholder="Архитектурные решения" disabled={busy} onChange={event => setTitle(event.target.value)} />
    </label>
    <button disabled={busy || !code.trim()}>{busy ? "Создаём…" : "Создать СВОР раздела"}</button>
    {error && <p className="error" role="alert">{error}</p>}
  </form>;
}

function SvorSet({ setId, revisionId, canEdit }: { setId: string; revisionId: string; canEdit: boolean }) {
  const cache = useQueryClient();
  const [kind, setKind] = useState<SourceKind>("original");
  const [roundId, setRoundId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const detail = useQuery({
    queryKey: ["svor-set", setId],
    queryFn: () => result(client.GET("/api/v1/svor-sets/{identity}", { params: { path: { identity: setId } } })),
  });
  const rounds = useQuery({
    queryKey: ["svor-rounds", setId],
    queryFn: () => result(client.GET("/api/v1/svor-sets/{identity}/rounds", { params: { path: { identity: setId } } })),
  });
  const record = detail.data;
  const currentRoundId = roundId || record?.latest_round?.id || "";
  const round = useQuery({
    queryKey: ["svor-round", currentRoundId],
    enabled: !!currentRoundId,
    queryFn: () => result(client.GET("/api/v1/svor-rounds/{identity}", { params: { path: { identity: currentRoundId } } })),
  });
  const refresh = () => Promise.all([
    cache.invalidateQueries({ queryKey: ["svor-set", setId] }),
    cache.invalidateQueries({ queryKey: ["svor-sets", revisionId] }),
    cache.invalidateQueries({ queryKey: ["svor-rounds", setId] }),
  ]);
  // Every mutation refreshes the set, even after a failure: a 409 means the version or sources changed elsewhere.
  const action: Action = async fn => {
    setBusy(true); setError("");
    try { await fn(); } catch (cause) { setError(message(cause)); }
    finally { await refresh(); setBusy(false); }
  };
  const history = rounds.data ?? [];
  const hasOriginal = !!record && record.sources_present.includes("original");
  return <>
    {detail.isPending && <p role="status" className="muted">Загружаем СВОР раздела…</p>}
    {detail.error && <p role="alert" className="error">{message(detail.error)}</p>}
    {record && <>
      <h3 className="svor-set-title">Раздел {record.section_code}{record.section_title ? ` · ${record.section_title}` : ""}</h3>
      {error && <p role="alert" className="error">{error}</p>}
      {busy && <p role="status" className="muted">Отправляем и обновляем данные…</p>}
      <nav className="svor-tabs" aria-label="Источники СВОР">
        {kinds.map(item => {
          const imported = importedSource(record.sources, item);
          const pending = pendingSources(record.sources, item).length;
          return <button type="button" key={item} aria-pressed={kind === item} onClick={() => setKind(item)}>
            {kindLabels[item]}
            <span className="counter">{imported ? (pending ? "принят · новая загрузка" : "принят") : pending ? "ждёт колонок" : "нет файла"}</span>
          </button>;
        })}
      </nav>
      <SourceTab key={kind} kind={kind} record={record} busy={busy} canEdit={canEdit} action={action} />
      <TechnicalPart record={record} revisionId={revisionId} busy={busy} canEdit={canEdit} action={action} />
      <section className="svor-round" aria-labelledby="svor-round-title">
        <div className="svor-round-heading">
          <div>
            <h3 id="svor-round-title">Раунд СВОР</h3>
            <p className="muted">Раунд неизменяем и хранит свои файлы. При тех же источниках и техчасти повторный расчёт
              возвращает прежний раунд. Подтверждать каждую позицию не требуется.</p>
          </div>
          {canEdit && <button type="button" className="primary" disabled={busy || !hasOriginal} onClick={() => void action(async () => {
            const created = await result(client.POST("/api/v1/svor-sets/{identity}/rounds", {
              params: { path: { identity: setId } }, body: { version: record.version },
            }));
            setRoundId(created.id);
          })}>{record.latest_round ? "Рассчитать новый раунд" : "Рассчитать раунд"}</button>}
        </div>
        {!hasOriginal && <p className="muted">Сначала загрузите и примите колонки исходной сметы — без неё раунд не считается.</p>}
        {rounds.error && <p role="alert" className="error">{message(rounds.error)}</p>}
        {history.length > 1 && <label className="field svor-round-select"><span>История раундов</span>
          <select value={currentRoundId} onChange={event => setRoundId(event.target.value)}>
            {history.map(item => <option key={item.id} value={item.id}>
              Раунд {item.number} · {date(item.created_at)} · {item.state === "clear" ? "замечаний нет" : "есть замечания"} · {roundHistoryText(item)}
            </option>)}
          </select>
        </label>}
        {currentRoundId && round.isPending && <p role="status" className="muted">Загружаем раунд…</p>}
        {round.error && <p role="alert" className="error">{message(round.error)}</p>}
        {round.data && <RoundView round={round.data} latest={round.data.id === record.latest_round?.id} />}
        {hasOriginal && !currentRoundId && !rounds.isPending && <p className="muted">Раунд ещё не рассчитан.</p>}
      </section>
    </>}
  </>;
}

function SourceTab({ kind, record, busy, canEdit, action }: { kind: SourceKind; record: SetOut; busy: boolean; canEdit: boolean; action: Action }) {
  const [pendingId, setPendingId] = useState("");
  const [editImported, setEditImported] = useState(false);
  const imported = importedSource(record.sources, kind);
  const pending = pendingSources(record.sources, kind);
  const replaced = replacedSources(record.sources, kind);
  const mapping = pending.find(item => item.id === pendingId) ?? pending[0] ?? (editImported ? imported : undefined);
  const books = record.sources.filter((source, index, all) => source.active && source.kind !== kind
    && all.findIndex(other => other.active && other.kind !== kind && other.sha256 === source.sha256) === index);
  const upload = (file: File) => action(async () => {
    const form = new FormData();
    form.append("file", file);
    const source = await call<SourceOut>(`/api/v1/svor-sets/${record.id}/sources?kind=${kind}`, { method: "POST", form });
    setPendingId(source.status === "uploaded" ? source.id : "");
    setEditImported(source.status === "imported");
  });
  const absent = kind === "original"
    ? "Файл исходной сметы не принят."
    : `Файл не принят. Без него ${kind === "excluded" ? "исключения" : "добавления и корректировки"} не учитываются — это отмечается в справке раунда.`;
  return <div className="svor-source">
    <p className="muted">{kindNotes[kind]}</p>
    {imported ? <div className="svor-file">
      <strong>{imported.filename}</strong>
      <span>{fileSize(imported.size)} · SHA-256 {imported.sha256.slice(0, 12)}…</span>
      <span>лист «{imported.sheet}», заголовки в строке {imported.header_row}</span>
      <span>позиций: {imported.positions}</span>
      <span>принят {date(imported.imported_at)}{imported.author_name ? ` · ${imported.author_name}` : ""}</span>
      <a href={imported.download_url} download>Скачать исходный файл</a>
      {canEdit && <button type="button" disabled={busy} onClick={() => setEditImported(!editImported)}>
        {editImported ? "Скрыть настройки колонок" : "Изменить лист или колонки"}
      </button>}
    </div> : <p>{absent}</p>}
    {imported && imported.warnings.some(warning => warning.startsWith("В И есть"))
      && <p role="alert">В исключениях смешаны знаки. Нажмите «Изменить лист или колонки» и выберите способ записи И.</p>}
    {imported && imported.warnings.length > 0 && <details><summary>Примечания импорта · {imported.warnings.length}</summary>
      <ul className="svor-warnings">{imported.warnings.map(warning => <li key={warning}>{warning}</li>)}</ul>
    </details>}
    {canEdit && <label className="field svor-upload"><span>{imported ? "Заменить файл (XLSX, до 25 МБ)" : "Загрузить файл (XLSX, до 25 МБ)"}</span>
      <input type="file" accept=".xlsx" disabled={busy} onChange={event => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (file) void upload(file);
      }} />
    </label>}
    {canEdit && !imported && !mapping && books.length > 0 && <div className="svor-reuse">
      <p className="muted">Если нужный лист в уже загруженной книге, повторно загружать её не нужно.</p>
      {books.map(book => <button type="button" disabled={busy} key={book.id} onClick={() => void action(async () => {
        const source = await result(client.POST("/api/v1/svor-sets/{identity}/reuse-source", {
          params: { path: { identity: record.id } }, body: { source_id: book.id, kind },
        }));
        setPendingId(source.id);
      })}>Выбрать лист из «{book.filename}»</button>)}
    </div>}
    {pending.length > 1 && <label className="field svor-upload"><span>Загрузки, ожидающие назначения колонок</span>
      <select value={mapping?.id ?? ""} onChange={event => setPendingId(event.target.value)}>
        {pending.map(item => <option key={item.id} value={item.id}>{item.filename} · {date(item.created_at)}</option>)}
      </select>
    </label>}
    {mapping && <SourceMapping key={mapping.id} source={mapping} busy={busy} canEdit={canEdit} action={action} />}
    {replaced.length > 0 && <details className="svor-history"><summary>История источников · {replaced.length}</summary>
      {replaced.map(item => <p key={item.id}>
        {item.filename} · {item.status === "duplicate" ? "копия принятого источника" : "заменён"} · {date(item.created_at)} · <a href={item.download_url} download>скачать</a>
      </p>)}
    </details>}
  </div>;
}

function SourceMapping({ source, busy, canEdit, action }: { source: SourceOut; busy: boolean; canEdit: boolean; action: Action }) {
  const [sheet, setSheet] = useState(source.sheet || "");
  const [headerRow, setHeaderRow] = useState(source.header_row ? String(source.header_row) : "");
  const [exclusionMode, setExclusionMode] = useState<NonNullable<Schema["SvorImportInput"]["exclusion_mode"]>>(source.exclusion_mode ?? "auto");
  const [headerDraft, setHeaderDraft] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Mapping>({});
  const query = useQuery({
    queryKey: ["svor-preview", source.id, sheet, headerRow],
    queryFn: () => result(client.GET("/api/v1/svor-sources/{identity}/preview", {
      params: { path: { identity: source.id }, query: { sheet: sheet || undefined, header_row: headerRow ? Number(headerRow) : undefined } },
    })),
    placeholderData: previous => previous,
  });
  const preview = query.data;
  const headerValue = headerDraft ?? String(preview?.header_row ?? "");
  const validHeader = /^\d+$/.test(headerValue) && Number(headerValue) >= 1 && Number(headerValue) <= 200;
  const previewCurrent = !!preview && !query.isFetching && !query.error && validHeader
    && Number(headerValue) === preview.header_row && (!sheet || sheet === preview.sheet);
  const applyHeader = () => {
    if (validHeader && Number(headerValue) !== preview?.header_row) setHeaderRow(String(Number(headerValue)));
  };
  useEffect(() => { if (preview) setMapping(source.sheet === preview.sheet && source.header_row === preview.header_row
    ? source.mapping : preview.proposed_mapping); }, [preview?.sheet, preview?.header_row]);
  const problems = mappingProblems(mapping);
  const submit = () => action(async () => {
    if (!preview || !previewCurrent) return;
    await result(client.POST("/api/v1/svor-sources/{identity}/import", {
      params: { path: { identity: source.id } },
      body: { sheet: preview.sheet, header_row: preview.header_row, mapping: cleanMapping(mapping), label: "", exclusion_mode: exclusionMode },
    }));
  });
  return <section className="svor-mapping" aria-label={`Колонки файла ${source.filename}`}>
    <h4>{source.filename} <small className="muted">{fileSize(source.size)} · SHA-256 {source.sha256.slice(0, 12)}… · загрузил {source.author_name || "—"} {date(source.created_at)}</small></h4>
    <p className="muted">{source.status === "imported" ? "Новые настройки сохранят отдельную версию источника."
      : "Файл сохранён, но ещё не принят."} Проверьте лист, строку заголовков и назначение колонок.</p>
    {query.isPending && <p role="status" className="muted">Читаем книгу и ищем строку заголовков…</p>}
    {query.error && <p role="alert" className="error">{message(query.error)}</p>}
    {preview && <>
      <div className="svor-fields">
        <label className="field"><span>Лист книги</span>
          <select value={preview.sheet} disabled={busy || query.isFetching} onChange={event => {
            setSheet(event.target.value); setHeaderRow(""); setHeaderDraft(null);
          }}>
            {preview.sheets.map(info => <option key={info.name} value={info.name}>{info.name}{info.rows != null ? ` · ${info.rows} строк` : ""}</option>)}
          </select>
        </label>
        <label className="field"><span>Строка заголовков</span>
          <input type="number" min={1} max={200} value={headerValue} disabled={busy || query.isFetching} aria-invalid={!validHeader}
            aria-describedby={!validHeader ? `svor-header-error-${source.id}` : undefined}
            onChange={event => setHeaderDraft(event.target.value)} onBlur={applyHeader}
            onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); applyHeader(); } }} />
          {!validHeader && <small className="error" id={`svor-header-error-${source.id}`}>Укажите целую строку от 1 до 200.</small>}
        </label>
      </div>
      {source.kind === "excluded" && <label className="field">
        <span>Как записаны исключения (И)</span>
        <select value={exclusionMode} disabled={busy} onChange={event => setExclusionMode(event.target.value as typeof exclusionMode)}>
          <option value="auto">Определить по знакам в файле</option>
          <option value="magnitude">Положительным объёмом: 1 означает убрать 1</option>
          <option value="signed">Со знаком изменения: −1 означает убрать 1</option>
        </select>
      </label>}
      {!preview.header_detected && <p className="muted">Строка заголовков не распознана автоматически: проверьте её номер и назначьте колонки вручную.</p>}
      {query.isFetching && <p className="muted" role="status">Обновляем образец строк…</p>}
      <fieldset className="source-picker" disabled={busy || !previewCurrent || !canEdit}><legend>Соответствие колонок</legend>
        <div className="svor-fields">{roles.map(({ role, label, required }) => <label className="field" key={role}>
          <span>{label}{required ? " · обязательна" : ""}</span>
          <select value={mapping[role] ?? ""} onChange={event => setMapping({ ...mapping, [role]: event.target.value || undefined })}>
            <option value="">Не назначена</option>
            {preview.columns.map(column => <option key={column.letter} value={column.letter}>{column.letter}{column.header ? ` · ${column.header}` : ""}</option>)}
          </select>
        </label>)}</div>
        <p className="muted">Номером позиции считается «№ в ЛСР». Формулы в колонке объёма не принимаются как проверенные числа; пустые объёмы остаются неизвестными.</p>
      </fieldset>
      {preview.notes.map(note => <p key={note} className="muted">{note}</p>)}
      <details>
        <summary>Образец строк · {preview.rows.length}</summary>
        <div className="svor-sample"><table>
          <thead><tr><th scope="col">Строка</th>{preview.columns.map(column => <th scope="col" key={column.letter}>{column.letter}<br /><small>{column.header}</small></th>)}</tr></thead>
          <tbody>{preview.rows.slice(0, 15).map((cells, index) => <tr key={preview.row_numbers[index]}>
            <td>{preview.row_numbers[index]}</td>
            {preview.columns.map((column, cellIndex) => <td key={column.letter}>
              {cells[cellIndex]?.formula ? <span title="Формула: значение не принимается как проверенное">{cells[cellIndex]?.value} ⚠</span> : cells[cellIndex]?.value}
            </td>)}
          </tr>)}</tbody>
        </table></div>
      </details>
      {canEdit && <button type="button" className="primary" disabled={busy || !previewCurrent || problems.length > 0} onClick={() => void submit()}>
        {busy ? "Принимаем…" : !previewCurrent ? "Проверьте лист и строку заголовков" : problems.length ? `Исправьте: ${problems.join("; ")}` : "Принять колонки и строки"}
      </button>}
    </>}
  </section>;
}

function TechnicalPart({ record, revisionId, busy, canEdit, action }: { record: SetOut; revisionId: string; busy: boolean; canEdit: boolean; action: Action }) {
  const [original, setOriginal] = useState(record.technical.original?.id ?? "");
  const [changed, setChanged] = useState(record.technical.changed?.id ?? "");
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  const documents = useQuery({
    queryKey: ["documents", revisionId],
    enabled: open && canEdit,
    queryFn: () => result(client.GET("/api/v1/revisions/{revision_id}/documents", { params: { path: { revision_id: revisionId } } })),
  });
  const attached = [record.technical.original, record.technical.changed].filter(Boolean).length;
  const attachedIds = new Set([record.technical.original?.id, record.technical.changed?.id]);
  const candidates = (documents.data ?? []).filter(doc => attachedIds.has(doc.id) || /\.(pdf|docx?)$/i.test(doc.path));
  const dirty = original !== (record.technical.original?.id ?? "") || changed !== (record.technical.changed?.id ?? "");
  return <details className="svor-technical" onToggle={event => setOpen(event.currentTarget.open)}>
    <summary>Техническая часть (PDF/DOCX) · {attached ? `привязано документов: ${attached}` : "не привязана"}</summary>
    <p className="muted">Техчасть привязывается к СВОР как документ редакции из «Материалов» (PDF или DOCX). В обосновании
      строк указывается только имя документа: соответствие ссылок его содержанию не проверяется автоматически и остаётся на специалисте.</p>
    {(["original", "changed"] as const).map(side => {
      const doc = record.technical[side];
      return <p key={side}><strong>{side === "original" ? "Исходная" : "Изменённая"}:</strong>{" "}
        {doc ? <a href={doc.url} target="_blank" rel="noreferrer">{doc.path}</a> : "не загружена / не проверена"}
        {doc && doc.status !== "ready" ? " · документ ещё не прочитан" : ""}</p>;
    })}
    {canEdit && <form onSubmit={event => {
      event.preventDefault();
      if (!reason.trim() || busy) return;
      void action(async () => {
        await result(client.PATCH("/api/v1/svor-sets/{identity}", {
          params: { path: { identity: record.id } },
          body: { version: record.version, technical_original_document_id: original || null, technical_changed_document_id: changed || null, reason: reason.trim() },
        }));
        setReason("");
      });
    }}>
      {documents.isFetching && <p role="status" className="muted">Загружаем документы редакции…</p>}
      {documents.error && <p role="alert" className="error">{message(documents.error)}</p>}
      {documents.data && candidates.length === 0 && <p className="muted">В материалах редакции нет PDF или DOCX: загрузите техчасть в «Материалы», затем привяжите её здесь.</p>}
      <label className="field"><span>Исходная техчасть</span>
        <select value={original} disabled={busy} onChange={event => setOriginal(event.target.value)}>
          <option value="">Не привязана</option>
          {candidates.map(doc => <option key={doc.id} value={doc.id}>{doc.path}</option>)}
        </select>
      </label>
      <label className="field"><span>Изменённая техчасть</span>
        <select value={changed} disabled={busy} onChange={event => setChanged(event.target.value)}>
          <option value="">Не привязана</option>
          {candidates.map(doc => <option key={doc.id} value={doc.id}>{doc.path}</option>)}
        </select>
      </label>
      <label className="field"><span>Основание изменения</span>
        <input value={reason} maxLength={4000} disabled={busy} placeholder="Например: получена техчасть изм. 2 от 15.09" onChange={event => setReason(event.target.value)} />
      </label>
      <button disabled={busy || !dirty || !reason.trim()}>Сохранить привязку</button>
    </form>}
  </details>;
}

function QuantityCell({ value, column }: { value: string | null; column: QuantityColumn }) {
  const cell = quantityCell(value, column);
  return <td className={`num svor-${cell.tone}`} title={cell.note || undefined}>
    {cell.text}{cell.tone === "correction" && <small>корректировка</small>}
  </td>;
}

function RoundView({ round, latest }: { round: RoundOut; latest: boolean }) {
  const [filter, setFilter] = useState<RowFilter>("all");
  const [protocolView, setProtocolView] = useState<"actions" | "classic">("actions");
  const positions = round.rows.filter(row => row.kind === "position");
  const changed = positions.filter(row => row.state !== "unchanged").length;
  const attention = positions.filter(row => row.state === "attention").length;
  const visible = filterRows(round.rows, filter);
  const remarks = [...round.remarks, ...round.carried];
  const counts = round.counts;
  return <>
    <div className={`svor-round-state ${round.state === "clear" ? "clear" : ""}`} role="status">
      <strong>Раунд {round.number} · {date(round.created_at)}{latest ? "" : " · сохранённый раунд, не последний"} · {round.state === "clear" ? "замечаний нет" : `замечаний: ${remarks.length}`}</strong>
      <p>{round.previous_round_id ? `По сравнению с предыдущим раундом: ${roundHistoryText(round)}.` : `Первый раунд: новых замечаний ${round.new}.`}</p>
      <p>Позиций {counts.positions ?? positions.length} · изменено {counts.changed ?? 0} · исключено {counts.excluded ?? 0} · добавлено {counts.added ?? 0} · требуют проверки {counts.attention ?? 0}{counts.unknown_results ? ` · без результата ${counts.unknown_results}` : ""}</p>
      <div className="svor-actions">
        <a href={round.files.xlsx} download>СВОР XLSX</a>
        <a href={round.files.docx} download>СВОР DOCX</a>
        <a href={round.files.zip} download>Комплект ZIP (СВОР, протокол, справка)</a>
      </div>
    </div>
    <div className="svor-table-heading">
      <h3>Ведомость · {formula}</h3>
      <label>Показать <select value={filter} onChange={event => setFilter(event.target.value as RowFilter)}>
        <option value="all">Все позиции · {positions.length}</option>
        <option value="changes">Изменения и добавления · {changed}</option>
        <option value="attention">Требуют проверки · {attention}</option>
      </select></label>
    </div>
    <p className="muted svor-legend">«—» в колонках Минус и Плюс — источника изменения нет. «Не установлено» — объём не задан в источнике
      и не заменён нулём. 0 — явный ноль. Отрицательный «Плюс» — корректировка в меньшую сторону.</p>
    <div className="svor-table-scroll">
      <table className="svor-table" aria-label={`СВОР, раунд ${round.number}`}>
        <thead><tr>
          <th scope="col">№</th><th scope="col">№ в ЛСР</th><th scope="col">Работа</th><th scope="col">Ед.</th>
          <th scope="col" className="num">Было</th><th scope="col" className="num">Минус</th><th scope="col" className="num">Плюс</th><th scope="col" className="num">Стало</th>
          <th scope="col">Состояние</th>
        </tr></thead>
        <tbody>{visible.map((row, index) => row.kind !== "position"
          ? <tr key={`${row.kind}-${index}`} className={`svor-${row.kind}`}><td colSpan={9}>{row.title}</td></tr>
          : <tr key={`${row.key}-${row.row_number ?? index}`}>
            <td className="num">{row.ordinal}</td>
            <td>{row.number}{row.marker ? ` ${row.marker}` : ""}</td>
            <td className="svor-work"><span>{row.description}</span>
              {row.norm_code && <small className="muted">{row.norm_code}</small>}
              {row.state !== "unchanged" && row.calculation && <small className="svor-calc">{row.calculation}</small>}
              {[...new Set(row.notes)].filter(note => !note.replace(/^[ИД]: /, "").startsWith("Количество взято из первой строки"))
                .map(note => <small key={note} className="svor-note">{note}</small>)}
            </td>
            <td>{row.unit}</td>
            <QuantityCell value={row.was} column="was" />
            <QuantityCell value={row.minus} column="minus" />
            <QuantityCell value={row.plus} column="plus" />
            <QuantityCell value={row.result} column="result" />
            <td>{row.state && <span className={`status ${stateClass[row.state]}`}>{stateLabels[row.state]}</span>}</td>
          </tr>)}</tbody>
      </table>
    </div>
    {visible.length === 0 && <p className="muted">Нет позиций в выбранном фильтре.</p>}
    <section className="svor-protocol" aria-labelledby="svor-protocol-title">
      <h3 id="svor-protocol-title">Протокол разногласий · {remarks.length}</h3>
      {remarks.length === 0 ? <p className="muted">Дубликатов, несопоставленных изменений, расхождений единиц и неизвестных объёмов не выявлено.</p> : <>
        <p className="svor-remark-summary">{remarkSummary(remarks).map(group => `${group.label} ${group.count}${group.carried ? ` (перенесено ${group.carried})` : ""}`).join(" · ")}</p>
        <div className="sap-view-toggle" role="group" aria-label="Вид протокола СВОР">
          <button type="button" aria-pressed={protocolView === "actions"} onClick={() => setProtocolView("actions")}>По действиям · Бета</button>
          <button type="button" aria-pressed={protocolView === "classic"} onClick={() => setProtocolView("classic")}>Классический</button>
        </div>
        {protocolView === "actions" ? <SvorActionProtocol key={round.id} round={round} latest={latest} /> : <div className="svor-remarks">{remarks.map((remark, index) => <article key={`${remark.key}-${index}`} className="svor-remark">
          <div><span className="status needs_review">{remark.label}</span>
            <small className="muted">{kindLabels[remark.source_kind]}{remark.carried_from ? " · перенесено из предыдущего раунда" : ""}</small></div>
          <p>{remark.message}</p>
          {remark.rows.length > 0 && <small>{remark.rows.map((row, rowIndex) => <a key={rowIndex} href={`/api/v1/svor-sources/${row.source_id}/download`} download>
            {row.filename || "файл источника"} · строка {row.row_number}</a>)}</small>}
        </article>)}</div>}
      </>}
    </section>
    <details className="svor-information">
      <summary>Информационная справка · {round.information.length}</summary>
      {round.information.map((note, index) => <p key={index}><strong>{note.work}</strong> · {note.status}: {note.reason}
        {note.sources.map((source, sourceIndex) => <span key={sourceIndex}> · {source[2] ? <a href={source[2]} target="_blank" rel="noreferrer">{source[0]}</a> : source[0]}</span>)}</p>)}
    </details>
    <details className="svor-sources">
      <summary>Источники раунда · {round.sources.length}</summary>
      {round.sources.map(source => <p key={source.id}><strong>{source.kind_label}</strong>: {source.filename} · лист «{source.sheet}», заголовки в строке {source.header_row ?? "—"} · SHA-256 {source.sha256.slice(0, 12)}… · <a href={source.url} download>скачать</a></p>)}
    </details>
  </>;
}
